import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Box, IconButton, Paper, Typography, makeStyles} from '@material-ui/core';
import {alpha} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import moment from 'moment';
import {getSSPLogo} from '../../../helpers/getSSPLogo';
import {computeDetailPanelPosition} from '../helpers/panelChangeLog';
import {
  formatPanelChangeDetailTitle,
  formatSspTypeLabel,
} from '../helpers/panelChangeGuides';

// Match PanelChangeDetailPanel footprint.
const PANEL_WIDTH = 380;
const PANEL_MAX_HEIGHT = 300;

const getGlassSurface = theme => ({
  backgroundColor:
    theme.palette.type === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
});

const useStyles = makeStyles(theme => {
  const glassSurface = getGlassSurface(theme);

  return {
    root: {
      position: 'absolute',
      zIndex: 30,
      width: PANEL_WIDTH,
      maxWidth: 'calc(100% - 16px)',
    },
    panel: {
      width: '100%',
      height: PANEL_MAX_HEIGHT,
      maxHeight: PANEL_MAX_HEIGHT,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[8],
      ...glassSurface,
    },
    arrowDownClip: {
      position: 'absolute',
      top: '100%',
      width: 16,
      height: 10,
      overflow: 'hidden',
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
      zIndex: 2,
    },
    arrowDown: {
      position: 'absolute',
      top: -6,
      left: '50%',
      width: 12,
      height: 12,
      marginLeft: -6,
      transform: 'rotate(45deg)',
      ...glassSurface,
      borderRight: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      pointerEvents: 'none',
    },
    arrowUpClip: {
      position: 'absolute',
      bottom: '100%',
      width: 16,
      height: 10,
      overflow: 'hidden',
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
      zIndex: 2,
    },
    arrowUp: {
      position: 'absolute',
      bottom: -6,
      left: '50%',
      width: 12,
      height: 12,
      marginLeft: -6,
      transform: 'rotate(45deg)',
      ...glassSurface,
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderTop: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      pointerEvents: 'none',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      flexShrink: 0,
      padding: theme.spacing(0.75, 0.75, 0.75, 1.25),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dateLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: '0.8125rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    closeButton: {
      flexShrink: 0,
      margin: theme.spacing(-0.25, 0),
    },
    list: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(0.5, 1.5, 1.5),
    },
    group: {
      marginBottom: theme.spacing(0.75),
      padding: theme.spacing(0.75, 1, 0.75, 0.875),
      borderRadius: 4,
      backgroundColor:
        theme.palette.type === 'dark'
          ? alpha(theme.palette.common.white, 0.04)
          : alpha(theme.palette.common.black, 0.03),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    groupHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(0.25),
      lineHeight: 1.35,
    },
    sspIcon: {
      height: 14,
      width: 14,
      flexShrink: 0,
      position: 'relative',
      top: 3,
      objectFit: 'contain',
    },
    sspIconPlaceholder: {
      width: 14,
      height: 14,
      flexShrink: 0,
    },
    sspName: {
      fontWeight: 700,
      lineHeight: 1.35,
      wordBreak: 'break-word',
    },
    values: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.25),
      flexWrap: 'wrap',
      paddingLeft: 18,
    },
    oldValue: {
      color: theme.palette.text.secondary,
      textDecoration: 'line-through',
      fontSize: '0.8125rem',
    },
    newValue: {
      color: theme.palette.success.main,
      fontWeight: 600,
      fontSize: '0.8125rem',
    },
    newValueReseller: {
      color: theme.palette.error.main,
      fontWeight: 600,
      fontSize: '0.8125rem',
    },
    arrow: {
      fontSize: 16,
      color: theme.palette.text.disabled,
    },
    time: {
      display: 'block',
      color: theme.palette.text.disabled,
      fontSize: '0.7rem',
      marginTop: 2,
      paddingLeft: 18,
    },
    empty: {
      padding: theme.spacing(2),
    },
  };
});

const formatChangeTime = timestamp => {
  const m = moment(timestamp);
  return m.isValid() ? m.format('HH:mm') : null;
};

const SspTypeChangePopup = ({
  date,
  changes = [],
  period,
  anchorRect,
  containerRef,
  attributeGroups = null,
  onClose,
}) => {
  const classes = useStyles();
  const rootRef = useRef(null);
  const [position, setPosition] = useState(null);

  const rows = useMemo(
    () =>
      (Array.isArray(changes) ? changes : []).map(change => {
        const toDirect = change.newValue === '0' || change.newValue === 0;
        return {
          ...change,
          logo: getSSPLogo(change.sspCode, attributeGroups),
          label: change.alias ? `${change.sspCode} (${change.alias})` : change.sspCode,
          oldLabel: formatSspTypeLabel(change.oldValue),
          newLabel: formatSspTypeLabel(change.newValue),
          toDirect,
          timeLabel: formatChangeTime(change.timestamp),
        };
      }),
    [changes, attributeGroups]
  );

  useLayoutEffect(() => {
    const containerEl = containerRef?.current;
    if (!anchorRect || !containerEl) {
      setPosition(null);
      return;
    }
    setPosition(
      computeDetailPanelPosition(anchorRect, containerEl, {
        width: PANEL_WIDTH,
        maxHeight: PANEL_MAX_HEIGHT,
      })
    );
  }, [anchorRect, containerRef, date, rows.length]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose?.();
    };
    const onPointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  if (!date) return null;

  return (
    <Box
      ref={rootRef}
      className={classes.root}
      style={
        position
          ? {left: position.left, top: position.top}
          : {visibility: 'hidden', pointerEvents: 'none'}
      }
    >
      <Paper className={classes.panel} elevation={8}>
        <Box className={classes.header}>
          <Typography variant="subtitle2" className={classes.dateLabel} noWrap>
            {formatPanelChangeDetailTitle(date, period)}
          </Typography>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={onClose}
            aria-label="Close SSP changes"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.list}>
          {rows.length === 0 ? (
            <Typography variant="body2" color="textSecondary" className={classes.empty}>
              No SSP changes for this day.
            </Typography>
          ) : (
            rows.map((row, index) => (
              <Box
                key={`${row.sspCode}-${row.alias || ''}-${row.timestamp || index}`}
                className={classes.group}
              >
                <Box className={classes.groupHeader}>
                  {row.logo ? (
                    <img src={row.logo} alt="" className={classes.sspIcon} />
                  ) : (
                    <span className={classes.sspIconPlaceholder} />
                  )}
                  <Typography component="span" variant="body2" className={classes.sspName}>
                    {row.label}
                  </Typography>
                </Box>
                <Box className={classes.values}>
                  {row.oldLabel !== '—' && (
                    <>
                      <Typography component="span" className={classes.oldValue}>
                        {row.oldLabel}
                      </Typography>
                      <ArrowRightAltIcon className={classes.arrow} />
                    </>
                  )}
                  <Typography
                    component="span"
                    className={row.toDirect ? classes.newValue : classes.newValueReseller}
                  >
                    {row.newLabel}
                  </Typography>
                </Box>
                {row.timeLabel && (
                  <Typography component="span" className={classes.time}>
                    {row.timeLabel}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </Paper>

      {position?.placement === 'above' && (
        <span className={classes.arrowDownClip} style={{left: position.arrowLeft}}>
          <span className={classes.arrowDown} />
        </span>
      )}
      {position?.placement === 'below' && (
        <span className={classes.arrowUpClip} style={{left: position.arrowLeft}}>
          <span className={classes.arrowUp} />
        </span>
      )}
    </Box>
  );
};

export default React.memo(SspTypeChangePopup);
